import {
  CreateButton,
  Datagrid,
  FilterButton,
  List,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';

/**
 * Store-tag vocabulary list (Phase 8, D-multi-channel-editor). The first-class
 * tag vocabulary (key / label / description) that the store editor assigns at the
 * per-market level and that rate rules eventually scope by (the stable `key`).
 *
 * REFERENCE DATA, not switcher-scoped: tags are platform-wide keys, so there is
 * no region filter here (assignment is per-market, in the store editor; the
 * vocabulary itself is global). Create + edit only — there is NO delete, because
 * a tag a rate rule may reference must not vanish (a future retire flag is the
 * additive, not a destroy). `q` matches the key / label / description.
 */

const tagFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
];

// Create is gated stores:write, so the affordance is hidden from a viewer (the
// route is guarded too — see StoreTagCreate).
const StoreTagListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'stores:write' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canWrite && <CreateButton />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const StoreTagListView = () => (
  <List
    resource="storeTags"
    title="Store tags"
    filters={tagFilters}
    actions={<StoreTagListActions />}
    sort={{ field: 'label', order: 'ASC' }}
    perPage={50}
    exporter={false}
  >
    {/* Clicking a row opens the tag edit. No bulk actions, no delete — tags are
        managed (create / edit), never destroyed. */}
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <TextField source="key" label="Key" />
      <TextField source="label" label="Label" />
      <TextField source="description" label="Description" />
    </Datagrid>
  </List>
);

/** Gated entry point registered as the `storeTags` Resource's list. */
export const StoreTagList = () => (
  <RequireAccess capability="stores:read">
    <StoreTagListView />
  </RequireAccess>
);
